import React, { ClassAttributes, ReactFragment, ReactNode } from 'react';
import { Alert } from 'react-bootstrap';
import { AppContextQuery } from '~/graphqlTypes/AppContextQuery';

import { formatIdentity } from '~/components/Identity/Identity';

import { RestrictedAccessProps } from './types';

export const _RestrictedAccessWrapperDiv = function RestrictedAccessWrapperDiv(
  props: ClassAttributes<HTMLDivElement> & { className?: string; children?: ReactNode },
) {
  return React.createElement( 'div', {
    ...props,
    className: `ops-restricted-access${props.className ? ` ${props.className}` : ''}`,
  } );
};

export const _RestrictedAccessWrapperFragment = function RestrictedAccessWrapperFragment(
  props: ClassAttributes<ReactFragment>,
) {
  return React.createElement( React.Fragment, props );
};

// TODO:
// function explainPermission( permissionCode ) {
//   const [resource, action, quantifier] = permissionCode.split( ':' );
// }
interface ExplainNotAllowedOptions {
  requiredPermissions: string[];
  context: AppContextQuery;
  viewName?: RestrictedAccessProps['viewName'];
  appId?: number;
}

export function explainNotAllowed( options: ExplainNotAllowedOptions ) {
  const { requiredPermissions, context, viewName = 'this part of the app', appId } = options;

  const requiresMultiplePermissions = requiredPermissions.length > 1;
  const { me } = context;
  const globalPermissions = me.permissions;
  const application = appId ? context.applications.find( ( _application ) => _application.id === appId ) : null;
  const applicationPermissions = application?.permissions || [];
  const applicationDescription = application
    ? `for application ${application.name} (<code>${application.id}</code>)`
    : 'globally';

  return (
    <Alert variant="info" className="ops-restricted-access__alert">
      <Alert.Heading>Access Denied</Alert.Heading>
      <p>
        Your account ({ formatIdentity( me ) }) does not have access to { viewName }.
      </p>
      { requiresMultiplePermissions ? (
        <>
          <p>Here are the permissions you’re missing:</p>
          <ul>
            { requiredPermissions.map( ( requiredPermission, index ) => (
              <li key={ index }>
                <code>{ requiredPermission }</code>
              </li>
            ) ) }
          </ul>
        </>
      ) : (
        <p>
          You are missing this permission: <code>{ requiredPermissions[0] }</code>.
        </p>
      ) }
      <p>Here are the global permissions you currently have:</p>
      <ul>
        { globalPermissions.map( ( myPermission, index ) => (
          <li key={ index }>
            <code>{ myPermission }</code>
          </li>
        ) ) }
      </ul>

      { appId && applicationPermissions.length && (
        <>
          <p>Here are the permissions you have { applicationDescription }:</p>
          <ul>
            { applicationPermissions.map( ( myPermission, index ) => (
              <li key={ index }>
                <code>{ myPermission }</code>
              </li>
            ) ) }
          </ul>
        </>
      ) }

      { appId && applicationPermissions.length === 0 && (
        <>
          <p>You do not have any permissions { applicationDescription }</p>
        </>
      ) }

      <p>
        If you feel this is in error, please ask an administrator to grant you the appropriate permissions, or raise an
        issue with the Product Team.
      </p>
    </Alert>
  );
}
